import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import { uploadProfilePhoto, deleteProfilePhoto, updateUserProfilePhoto } from '../utils/profilePhotos';
import { Save, Lock, Edit2, X, Check, AlertCircle, User, Mail, Building, Camera, Trash2 } from 'lucide-react';
import LiquidGlass from './LiquidGlass';

export default function Profile() {
  const { user } = useAuth();
  const { userProfile, organization, refetch, loading: orgLoading } = useOrganization();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    bio: '',
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Create default profile if it doesn't exist
  useEffect(() => {
    const createDefaultProfile = async () => {
      if (user?.id && !orgLoading && userProfile === null) {
        try {
          console.log('Creating default profile for user:', user.id);
          
          // First check if profile already exists
          const { data: existingProfile } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('user_id', user.id)
            .single();

          if (existingProfile) {
            console.log('Profile already exists, refetching...');
            await refetch();
            return;
          }

          const { data, error } = await supabase
            .from('user_profiles')
            .insert({
              user_id: user.id,
              first_name: null,
              last_name: null,
              bio: null,
              theme: 'light',
              role: 'Member'
            })
            .select()
            .single();

          if (error) {
            console.error('Error creating default profile:', error);
            setError(`Failed to create profile: ${error.message}`);
          } else {
            console.log('Default profile created successfully:', data);
            // Refetch to get the newly created profile
            await refetch();
          }
        } catch (err: any) {
          console.error('Error creating default profile:', err);
          setError(`Failed to create profile: ${err.message}`);
        }
      }
    };

    createDefaultProfile();
  }, [user?.id, userProfile, orgLoading, refetch]);

  useEffect(() => {
    if (user) {
      console.log('Setting form data from user and userProfile:', { user: user.email, userProfile });
      setFormData({
        email: user.email || '',
        first_name: userProfile?.first_name || '',
        last_name: userProfile?.last_name || '',
        bio: userProfile?.bio || '',
      });
    }
  }, [user, userProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    console.log('=== PROFILE SAVE DEBUG ===');
    console.log('Form data being saved:', formData);
    console.log('User ID:', user?.id);
    console.log('Current userProfile:', userProfile);

    try {
      // Update email if changed
      if (formData.email !== user?.email) {
        console.log('Updating email from', user?.email, 'to', formData.email);
        const { error: emailError } = await supabase.auth.updateUser({
          email: formData.email
        });
        if (emailError) {
          console.error('Email update error:', emailError);
          throw emailError;
        }
        console.log('Email updated successfully');
      }

      // Prepare profile data
      const profileUpdateData = {
        user_id: user?.id,
        first_name: formData.first_name.trim() || null,
        last_name: formData.last_name.trim() || null,
        bio: formData.bio.trim() || null,
        updated_at: new Date().toISOString()
      };

      console.log('Profile data being upserted:', profileUpdateData);

      // Try upsert first
      const { data: upsertData, error: upsertError } = await supabase
        .from('user_profiles')
        .upsert(profileUpdateData, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      console.log('Upsert result:', { data: upsertData, error: upsertError });

      if (upsertError) {
        console.error('Upsert failed, trying insert:', upsertError);
        
        // If upsert fails, try insert
        const { data: insertData, error: insertError } = await supabase
          .from('user_profiles')
          .insert(profileUpdateData)
          .select()
          .single();

        console.log('Insert result:', { data: insertData, error: insertError });

        if (insertError) {
          console.error('Insert also failed:', insertError);
          throw insertError;
        }
      }
      
      console.log('Profile operation successful, calling refetch...');
      
      // Force a fresh fetch of the profile data
      await new Promise(resolve => setTimeout(resolve, 100)); // Small delay to ensure DB consistency
      await refetch();
      
      console.log('After refetch, userProfile is now:', userProfile);
      
      setEditMode(false);
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (!passwordData.currentPassword) {
      setPasswordError('Current password is required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    try {
      // First verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: passwordData.currentPassword
      });

      if (signInError) {
        setPasswordError('Current password is incorrect');
        return;
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;
      
      setPasswordError('Password updated successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      console.error('Error updating password:', err);
      setPasswordError(err.message || 'Failed to update password. Please try again.');
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploadingPhoto(true);
    setError(null);

    try {
      // Delete old photo if exists
      if (userProfile?.profile_photo_url) {
        try {
          await deleteProfilePhoto(userProfile.profile_photo_url);
        } catch (err) {
          console.warn('Failed to delete old photo:', err);
        }
      }

      // Upload new photo
      const photoUrl = await uploadProfilePhoto(file, user.id);
      
      // Update profile with new photo URL
      await updateUserProfilePhoto(user.id, photoUrl);
      
      await refetch();
      setSuccess('Profile photo updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!userProfile?.profile_photo_url || !user) return;

    setUploadingPhoto(true);
    setError(null);

    try {
      await deleteProfilePhoto(userProfile.profile_photo_url);
      await updateUserProfilePhoto(user.id, null);
      await refetch();
      setSuccess('Profile photo removed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setError(null);
    // Reset form data to current profile values
    if (user) {
      setFormData({
        email: user.email || '',
        first_name: userProfile?.first_name || '',
        last_name: userProfile?.last_name || '',
        bio: userProfile?.bio || '',
      });
    }
  };

  const getInitials = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name[0]}${userProfile.last_name[0]}`.toUpperCase();
    }
    if (userProfile?.first_name) {
      return userProfile.first_name[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Please sign in to access your profile.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Profile</h1>
      </div>

      {error && (
        <LiquidGlass className="bg-red-100/60 border-red-200/60 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        </LiquidGlass>
      )}

      {success && (
        <LiquidGlass className="bg-green-100/60 border-green-200/60 p-4">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="w-5 h-5" />
            {success}
          </div>
        </LiquidGlass>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Photo Section */}
        <LiquidGlass variant="card" className="p-6">
          <div className="text-center">
            <div className="relative inline-block">
              {userProfile?.profile_photo_url ? (
                <img
                  src={userProfile.profile_photo_url}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover mx-auto"
                  loading="lazy"
                />
              ) : (
                <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-3xl font-bold text-gray-600">{getInitials()}</span>
                </div>
              )}
              
              {!uploadingPhoto && (
                <div className="absolute bottom-0 right-0">
                  {userProfile?.profile_photo_url ? (
                    // Show delete button when photo exists
                    <button
                      onClick={handlePhotoDelete}
                      className="w-10 h-10 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors flex items-center justify-center"
                      title="Remove photo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : (
                    // Show upload button when no photo exists
                    <label className="w-10 h-10 bg-gray-900 text-white rounded-full cursor-pointer hover:bg-gray-800 transition-colors flex items-center justify-center" title="Upload photo">
                      <Camera className="w-4 h-4" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              )}
              
              {uploadingPhoto && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                </div>
              )}
            </div>
            
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {userProfile?.first_name && userProfile?.last_name
                  ? `${userProfile.first_name} ${userProfile.last_name}`
                  : user.email}
              </h3>
              <p className="text-gray-600">{userProfile?.role}</p>
              {organization && (
                <p className="text-sm text-gray-500">{organization.name}</p>
              )}
            </div>
          </div>
        </LiquidGlass>

        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          <LiquidGlass variant="card" className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-5 h-5 text-gray-700" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-800">Profile Information</h2>
                  <p className="text-sm text-gray-500">Update your account details</p>
                </div>
                {!editMode && (
                  <button
                    type="button"
                    onClick={() => setEditMode(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-white/30 rounded-md transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      disabled={!editMode}
                      className="block w-full pl-10 pr-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      disabled={!editMode}
                      className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200"
                      placeholder="First Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      disabled={!editMode}
                      className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200"
                      placeholder="Last Name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Bio</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    disabled={!editMode}
                    rows={4}
                    className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200"
                    placeholder="Tell us a bit about yourself"
                  />
                </div>
              </div>

              {editMode && (
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-white/30 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 text-white font-medium disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </LiquidGlass>
                </div>
              )}
            </form>
          </LiquidGlass>

          {/* Password Reset Card */}
          <LiquidGlass variant="card" className="p-6">
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Lock className="w-5 h-5 text-gray-700" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Change Password</h2>
                  <p className="text-sm text-gray-500">
                    Update your password to keep your account secure
                  </p>
                </div>
              </div>

              {passwordError && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                  passwordError.includes('successfully')
                    ? 'bg-green-100/80 border-green-200/80 text-green-700'
                    : 'bg-red-100/80 border-red-200/80 text-red-700'
                }`}>
                  <AlertCircle className="w-5 h-5" />
                  {passwordError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Password</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">New Password</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div>
                <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-medium"
                  >
                    <Lock className="w-4 h-4" />
                    Update Password
                  </button>
                </LiquidGlass>
              </div>
            </form>
          </LiquidGlass>
        </div>
      </div>
    </div>
  );
}